import sha1 from 'js-sha1';

// extended_universe is always part of the SHA — both true and false
// branches produce a non-empty marker. Two reasons:
// 1. A fresh form submit for the same gene list always lands on a
//    different hash from any legacy URL minted before the EU work,
//    so the cache miss forces a recompute under the current
//    enrichment math instead of serving a stale cached result.
// 2. URL paste (body_tag-only requests with no entrez body) still
//    works because the backend uses the URL's hash directly as the
//    cache key without re-validating against the canonical SHA.
//    Old URLs continue to resolve their legacy cache entries.
// Do NOT collapse the false branch back to '' — that would
// reintroduce the cache-poisoning vector this commit fixed.
export const getCommunityBodyTag = (
  genes,
  networkSlug,
  giantVersion,
  title = '',
  extendedUniverse = false,
) => {
  const body = `{ "entrez": [${genes
    .map(gene => `"${gene.entrez}"`)
    .sort()}] }`;
  const euMarker = extendedUniverse
    ? 'extended_universe=true'
    : 'extended_universe=false';
  return sha1(`${body}${networkSlug}${giantVersion}${title}${euMarker}`);
};

// Cluster id of the selected row, or '' when nothing is selected
export const selectedCommunityId = (sortedData, selectedCommunity) => {
  if (selectedCommunity !== '' && sortedData.length) {
    // Back/Forward can restore an index past the end of freshly reset data
    return sortedData[Number(selectedCommunity)]?.[0] ?? '';
  }
  return '';
};

// What a history entry stores: the community state plus the module-list filters it
// was made under (term database, hide-unenriched), which decide what each row index means
export const historyEntryState = (
  storedState,
  selectedTermDbSlug,
  filterUnenrichedModules,
) => ({ ...storedState, selectedTermDbSlug, filterUnenrichedModules });

// The term database to reapply after Back/Forward reloads a result, or null
// when the entry has none or the reloaded result lacks it
export const restoredTermDb = (entryTermDb, enrichment) =>
  enrichment?.[entryTermDb] ? entryTermDb : null;

// Nodes of the selected module, or [] when there is none
export const selectedCommunityNodes = (sortedData, selectedCommunity) => {
  if (selectedCommunity === '') return [];
  // Back/Forward can restore an index past the end of freshly reset data
  return sortedData[Number(selectedCommunity)]?.[1].nodes ?? [];
};

// The selected gene, or '' when a newly loaded result does not contain it.
// Node ids are positions within a result, so across results the gene must
// match by Entrez id; re-deriving the same result keeps the same node object.
export const keptSelectedGene = (selectedGene, previousLookup, nextLookup) => {
  const node = nextLookup[selectedGene];
  const previous = previousLookup[selectedGene];
  if (node && node === previous) return selectedGene;
  return node?.entrez && node.entrez === previous?.entrez ? selectedGene : '';
};

// History state for an Overview or Module tab click; Overview clears the module
export const tabHistoryState = (selectedTabKey, selectedCommunity) => ({
  // Back/Forward reloads the result, so the entry must carry the module
  selectedCommunity: selectedTabKey === 1 ? '' : selectedCommunity,
  selectedEdges: [],
  selectedTerm: {},
});

// History state for a term click, with the selected gene's edges in that term
export const termHistoryState = (term, selectedGene, selectedCommunity) => {
  const state = { selectedTerm: term, selectedCommunity };
  if (selectedGene !== '') {
    state.selectedEdges = term.edges.filter(
      e => e.source === selectedGene || e.target === selectedGene,
    );
  }
  return state;
};
